import React from 'react';
import { Box, Typography, Breadcrumbs, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  title: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: React.ReactNode;
  backButton?: React.ReactNode;
  dense?: boolean;
}

/**
 * Page header component with title, breadcrumbs, and actions
 */
export const PageHeader: React.FC<PageHeaderProps> = ({ 
  title, 
  breadcrumbs, 
  actions,
  backButton,
  dense = false,
}) => {
  return (
    <Box sx={{ mb: dense ? 0 : { xs: 2, sm: 3 } }}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          sx={{ 
            mb: dense ? 0.5 : 1, 
            '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap' },
            '& .MuiBreadcrumbs-li': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
          }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            
            if (isLast || !crumb.to) {
              return (
                <Typography key={index} color="text.primary" sx={{ fontSize: 'inherit' }}>
                  {crumb.label}
                </Typography>
              );
            }
            
            return (
              <MuiLink
                key={index}
                component={RouterLink}
                to={crumb.to}
                color="inherit"
                underline="hover"
                sx={{ fontSize: 'inherit' }}
              >
                {crumb.label}
              </MuiLink>
            );
          })}
        </Breadcrumbs>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: dense ? { xs: 0.75, sm: 0.5 } : { xs: 1, sm: 0 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          {backButton}
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              fontSize: dense
                ? { xs: '1.35rem', sm: '1.5rem', md: '1.65rem' }
                : { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
              lineHeight: dense ? 1.2 : undefined,
              fontWeight: 500,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
        </Box>
        {actions && (
          <Box sx={{ 
            display: 'flex', 
            gap: dense ? { xs: 0.5, sm: 0.75 } : { xs: 0.5, sm: 1 },
            flexWrap: 'wrap',
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'flex-end', sm: 'flex-start' }
          }}>
            {actions}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PageHeader;
